import { CURRENT_STAGE, PARALLEL_FROM_STAGE, STAGES, type Stage } from './modules';
import data from './requirements.json';

export type RequirementStatus = 'planned' | 'in_progress' | 'done' | 'partial';

/** Почему требование ещё не сделано или сделано не полностью */
export type HoldKind =
  /** Ещё не дошла очередь по плану этапов */
  | 'stage'
  /** Вторая или третья очередь ТЗ — после приёмки первой */
  | 'queue'
  /** Ждёт другой раздел или требование */
  | 'dependency'
  /** Ждёт решения заказчика */
  | 'decision'
  /** Нужны серверы, доступы, аккаунты или данные со стороны заказчика или внешней системы */
  | 'external'
  /** Сделано по прежней редакции ТЗ, переделывается под новую */
  | 'rework'
  /** Остаток проверяется при приёмке первой очереди */
  | 'acceptance';

export interface RequirementHold {
  kind: HoldKind;
  text: string;
}

/** Где в портале требование работает: страница и место на ней */
export interface RequirementPlace {
  /** Путь в веб-приложении; без пути — весь портал или инфраструктура */
  path?: string;
  label: string;
}

/** Требование ТЗ (разделы 6–9) с очередью внедрения, этапом разработки и состоянием в портале. */
export interface Requirement {
  code: string;
  module: string;
  section: string;
  group: string;
  text: string;
  /** Очередь внедрения по ТЗ: 1, 2 или 3 */
  priority: number;
  /** Этап разработки, на котором требование реализуется (для готового — на котором выложено) */
  stage: number;
  status: RequirementStatus;
  /** Что сделано или что осталось — короткая пометка разработчика */
  note?: string;
  /** Дата выкладки на стенд (для готового и частично готового) */
  since?: string;
  /** Где в портале это работает */
  where?: RequirementPlace[];
  /** Почему ещё не сделано или сделано не полностью */
  hold?: RequirementHold;
  /** Дата, когда требование появилось в ТЗ после первой редакции реестра */
  added?: string;
}

/**
 * Реестр правят несколько чатов руками: поле `where` иногда записывают строкой, а не списком мест.
 * Чтобы одна такая запись не ломала «Ход разработки», строка превращается в одно место без пути.
 */
function normalize(r: Requirement & { where?: unknown }): Requirement {
  const w = r.where;
  if (w === undefined || Array.isArray(w)) return r as Requirement;
  return { ...r, where: typeof w === 'string' && w ? [{ label: w }] : [] };
}

export const REQUIREMENTS = (data as (Requirement & { where?: unknown })[]).map(normalize);

export const REQUIREMENT_STATUS_TITLES: Record<RequirementStatus, string> = {
  planned: 'Запланировано',
  in_progress: 'В работе',
  partial: 'Частично',
  done: 'Готово',
};

export const HOLD_TITLES: Record<HoldKind, string> = {
  stage: 'Ждёт своего этапа',
  queue: 'Вторая и третья очередь ТЗ',
  dependency: 'Зависит от другого раздела',
  decision: 'Ждёт решения заказчика',
  external: 'Нужно со стороны заказчика',
  rework: 'Переделка под новую редакцию ТЗ',
  acceptance: 'Проверяется при приёмке',
};

const LAST_STAGE = STAGES[STAGES.length - 1]!.number;

/** «ГГГГ-ММ-ДД» → «ДД.ММ.ГГГГ» без привязки к часовому поясу браузера */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export function stageOf(r: Requirement): Stage | undefined {
  return STAGES.find((s) => s.number === r.stage);
}

/**
 * Состояние этапа: выложен (этапы до параллельных), идёт сейчас (этапы, которые чаты ведут одновременно),
 * следующий, в плане или после пилота
 */
export type StageState = 'released' | 'current' | 'next' | 'later' | 'after_pilot';

export function stageState(s: Stage): StageState {
  if (s.number === LAST_STAGE) return 'after_pilot';
  if (s.number < PARALLEL_FROM_STAGE && s.releasedAt) return 'released';
  if (s.number <= CURRENT_STAGE) return 'current';
  if (s.number === CURRENT_STAGE + 1) return 'next';
  return 'later';
}

export const STAGE_STATE_TITLES: Record<StageState, string> = {
  released: 'выложен',
  current: 'идёт сейчас',
  next: 'следующий',
  later: 'в плане',
  after_pilot: 'после пилота',
};

/** Когда: для готового — дата выкладки, для остального — этап и его место в плане */
export function whenOf(r: Requirement): string {
  const s = stageOf(r);
  if (r.status === 'done') return r.since ? `Выложено ${formatDay(r.since)}` : 'Выложено';
  if (r.status === 'partial') return r.since ? `Часть выложена ${formatDay(r.since)}` : 'Часть выложена';
  if (!s) return `Этап ${r.stage}`;
  const st = stageState(s);
  if (st === 'after_pilot') return `Этап ${s.number} «${s.title}» — после приёмки первой очереди`;
  if (st === 'current') return `Этап ${s.number} «${s.title}» — идёт сейчас`;
  if (st === 'next') return `Этап ${s.number} «${s.title}» — следующий`;
  return `Этап ${s.number} «${s.title}»`;
}

/** Почему ещё нет: явная причина из реестра или вывод из очереди и этапа */
export function holdOf(r: Requirement): RequirementHold | null {
  if (r.status === 'done') return null;
  if (r.hold) return r.hold;
  if (r.priority > 1) {
    return {
      kind: 'queue',
      text: `${r.priority} очередь по ТЗ — после приёмки первой очереди на пилоте; порядок уточняет владелец (раздел 9.1 ТЗ)`,
    };
  }
  const s = stageOf(r);
  const needs = s?.needs ? `; заранее нужно: ${s.needs}` : '';
  if (s && stageState(s) === 'current')
    return { kind: 'stage', text: `Этап ${s.number} идёт, до этого требования ещё не дошли${needs}` };
  return { kind: 'stage', text: `Ещё не дошла очередь по плану этапов${needs}` };
}
